// Standard-MIDI-Datei (Format 0/1) lesen und schreiben. Ergebnis in Beats, damit es zum Projekt-Tempo passt.

export interface MidiNote { t: number; d: number; m: number; v: number; channel: number; track: number }

export function parseMidi(buf: ArrayBuffer): { notes: MidiNote[]; ppq: number; bpm?: number; trackNames: string[] } {
  const v = new DataView(buf);
  let p = 0;
  const str = (n: number) => { let s = ''; for (let i = 0; i < n; i++) s += String.fromCharCode(v.getUint8(p + i)); p += n; return s; };
  const u32 = () => { const x = v.getUint32(p); p += 4; return x; };
  const u16 = () => { const x = v.getUint16(p); p += 2; return x; };
  const vlq = () => { let x = 0, b; do { b = v.getUint8(p++); x = (x << 7) | (b & 0x7f); } while (b & 0x80); return x; };
  if (str(4) !== 'MThd') throw new Error('Keine MIDI-Datei');
  const hlen = u32(); u16(); const ntrk = u16(); const division = u16(); p += hlen - 6;
  if (division & 0x8000) throw new Error('SMPTE-Zeitbasis wird nicht unterstützt');
  const ppq = division;
  const notes: MidiNote[] = [];
  let bpm: number | undefined;
  const trackNames: string[] = [];
  for (let tr = 0; tr < ntrk && p < v.byteLength; tr++) {
    if (str(4) !== 'MTrk') throw new Error('Defekte Spur');
    const end = u32() + p;
    let tick = 0, status = 0;
    const open = new Map<number, { tick: number; vel: number }>();
    while (p < end) {
      tick += vlq();
      let b = v.getUint8(p);
      if (b & 0x80) { status = b; p++; } else b = status;
      const type = status & 0xf0, ch = status & 0x0f;
      if (status === 0xff) {
        const meta = v.getUint8(p++); const len = vlq();
        if (meta === 0x03 && !trackNames[tr]) { let n = ''; for (let i = 0; i < len; i++) n += String.fromCharCode(v.getUint8(p + i)); trackNames[tr] = n.replace(/[\x00-\x1f]/g, '').trim(); }
        if (meta === 0x51 && bpm === undefined) bpm = 60000000 / ((v.getUint8(p) << 16) | (v.getUint8(p + 1) << 8) | v.getUint8(p + 2));
        p += len;
      } else if (status === 0xf0 || status === 0xf7) { p += vlq(); }
      else if (type === 0x90 || type === 0x80) {
        const m = v.getUint8(p++), vel = v.getUint8(p++), key = ch * 128 + m;
        if (type === 0x90 && vel > 0) open.set(key, { tick, vel });
        else { const o = open.get(key); if (o) { notes.push({ t: o.tick / ppq, d: Math.max(1, tick - o.tick) / ppq, m, v: o.vel / 127, channel: ch, track: tr }); open.delete(key); } }
      } else if (type === 0xc0 || type === 0xd0) p += 1;
      else p += 2;
    }
    p = end;
  }
  notes.sort((a, b) => a.t - b.t || a.m - b.m);
  return { notes, ppq, bpm, trackNames };
}

/** Noten (in Beats) als MIDI-Datei Format 0 – für Export in Pro Tools, Logic, Ableton */
export function writeMidi(notes: { t: number; d: number; m: number; v: number }[], bpm: number, ppq = 480): Uint8Array {
  const ev: { tick: number; bytes: number[] }[] = [];
  const tempo = Math.round(60000000 / bpm);
  ev.push({ tick: 0, bytes: [0xff, 0x51, 3, (tempo >> 16) & 255, (tempo >> 8) & 255, tempo & 255] });
  for (const n of notes) {
    ev.push({ tick: Math.round(n.t * ppq), bytes: [0x90, n.m, Math.max(1, Math.round(n.v * 127))] });
    ev.push({ tick: Math.round((n.t + n.d) * ppq), bytes: [0x80, n.m, 0] });
  }
  ev.sort((a, b) => a.tick - b.tick || (a.bytes[0] === 0x80 ? -1 : 1));
  const body: number[] = [];
  const vlq = (x: number) => { const out = [x & 0x7f]; while ((x >>= 7)) out.unshift((x & 0x7f) | 0x80); body.push(...out); };
  let last = 0;
  for (const e of ev) { vlq(e.tick - last); last = e.tick; body.push(...e.bytes); }
  body.push(0, 0xff, 0x2f, 0);
  const u32 = (x: number) => [(x >>> 24) & 255, (x >>> 16) & 255, (x >>> 8) & 255, x & 255];
  return new Uint8Array([0x4d, 0x54, 0x68, 0x64, ...u32(6), 0, 0, 0, 1, (ppq >> 8) & 255, ppq & 255, 0x4d, 0x54, 0x72, 0x6b, ...u32(body.length), ...body]);
}
